/**
 * PetCareScheduleBlock — inline editor for the "pet nerd" care schedule:
 * next vet date, flea/tick interval in days, and the optional cat
 * litter-change tracker toggle. Only renders for dogs and cats.
 *
 * Rendered inside both PetDialog (dogs) and PetCareDialog (cats and
 * other non-dog pets) so the UI is consistent across species. On the
 * Free tier, only the primary (first-added) dog/cat sees the editor;
 * other pets get a locked preview with an "Upgrade to Plus" CTA.
 *
 * Stamping the dates themselves (bath done, pill given) lives on
 * PetsPage via PetCareExtras — this block is for settings that
 * wouldn't fit as a one-tap button.
 */

import React, { useState, useEffect } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isPetCareLockedForPet } from "@/lib/petCareGate";
import { shouldPulseCareUpgrade, markCareUpgradePulsed } from "@/lib/walkUpgradePulse";

export default function PetCareScheduleBlock({
  pet,
  members,
  tier,
  onPetUpdated,
  onOpenUpgrade,
}) {
  const species = pet?.pet_species;
  const isDog = species === "dog";
  const isCat = species === "cat";
  // Tier gate computed BEFORE any early return so the pulse hooks below
  // never violate React's rules-of-hooks ordering. Locked is a pure
  // function of props, so this is cheap to compute on every render.
  const locked = (isDog || isCat) && isPetCareLockedForPet(pet, members, tier);
  const [carePulse] = useState(() => locked && shouldPulseCareUpgrade(pet?.id));
  useEffect(() => {
    if (carePulse) markCareUpgradePulsed(pet?.id);
  }, [carePulse, pet?.id]);
  const [editing, setEditing] = useState(false);
  const [vetNext, setVetNext] = useState(pet?.vet_wellness_next || "");
  const [fleaInterval, setFleaInterval] = useState(
    pet?.flea_tick_interval_days || 30,
  );
  const [litterOn, setLitterOn] = useState(!!pet?.litter_enabled);
  const [foodReminder, setFoodReminder] = useState(!!pet?.food_reminder_enabled);
  const [waterReminder, setWaterReminder] = useState(!!pet?.water_reminder_enabled);
  const [saving, setSaving] = useState(false);

  // Refresh form fields whenever the pet prop changes — e.g. another
  // member's care log came through and mutated the doc upstream.
  useEffect(() => {
    setVetNext(pet?.vet_wellness_next || "");
    setFleaInterval(pet?.flea_tick_interval_days || 30);
    setLitterOn(!!pet?.litter_enabled);
    setFoodReminder(!!pet?.food_reminder_enabled);
    setWaterReminder(!!pet?.water_reminder_enabled);
  }, [pet?.id, pet?.vet_wellness_next, pet?.flea_tick_interval_days, pet?.litter_enabled, pet?.food_reminder_enabled, pet?.water_reminder_enabled]);

  if (!isDog && !isCat) return null;

  if (locked) {
    const upgradeTo = tier === "free" ? "Plus" : "Grand";
    const reason =
      tier === "free"
        ? "The Free plan includes the care schedule for your first dog or cat. Upgrade to Plus to add a second — or Grand for every dog & cat."
        : "The Plus plan includes the care schedule for two dogs or cats. Upgrade to Grand to unlock it for every pet.";
    return (
      <div
        className="mt-4 pt-4 border-t border-[color:var(--line-soft)]"
        data-testid={`pet-care-schedule-locked-${pet.id}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-3.5 h-3.5 text-[color:var(--ink-3)]" strokeWidth={2} />
          <div className="label-tiny">Care schedule · {upgradeTo} and up</div>
        </div>
        <p className="text-[13px] text-[color:var(--ink-2)] leading-snug">
          {reason}
        </p>
        {onOpenUpgrade && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenUpgrade();
            }}
            className={`ff-btn mt-3${carePulse ? " ff-care-upgrade-pulse" : ""}`}
            data-testid={`pet-care-upgrade-${pet.id}`}
          >
            Upgrade to {upgradeTo} →
          </button>
        )}
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        vet_wellness_next: vetNext || "",
        flea_tick_interval_days: Number(fleaInterval) || 30,
        food_reminder_enabled: foodReminder,
        water_reminder_enabled: waterReminder,
      };
      if (isCat) payload.litter_enabled = litterOn;
      const updated = await api.updateMember(pet.id, payload);
      onPetUpdated && onPetUpdated(updated);
      toast.success("Care schedule saved");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  // Inline-toggle the food/water reminders straight from the read-only
  // card. Pet owners shouldn't need to dig into "Edit → Save schedule"
  // just to flip a notification on; they want one tap. We post the
  // single field in isolation (no vet/flea churn) and reflect the
  // server's confirmed value back into local state once it returns,
  // so a network failure doesn't leave the UI lying.
  const toggleReminder = async (which) => {
    if (saving) return;
    const next = which === "food" ? !foodReminder : !waterReminder;
    if (which === "food") setFoodReminder(next);
    else setWaterReminder(next);
    try {
      const payload =
        which === "food"
          ? { food_reminder_enabled: next }
          : { water_reminder_enabled: next };
      const updated = await api.updateMember(pet.id, payload);
      onPetUpdated && onPetUpdated(updated);
    } catch (e) {
      // Roll back UI on failure.
      if (which === "food") setFoodReminder(!next);
      else setWaterReminder(!next);
      toast.error(e?.response?.data?.detail || "Couldn't update reminder");
    }
  };

  // Compute human-readable sub-labels so the card reads like a readable
  // summary instead of a raw database dump — "in 342 days" is much more
  // scannable than "2027-04-21".
  const daysUntil = (iso) => {
    if (!iso) return null;
    try {
      const target = new Date(`${iso}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.round((target - today) / 86400000);
      if (days < 0) return `${-days}d overdue`;
      if (days === 0) return "today";
      if (days === 1) return "tomorrow";
      if (days < 60) return `in ${days}d`;
      if (days < 365) return `in ~${Math.round(days / 30)} months`;
      return `in ~${Math.round(days / 30)} months`;
    } catch {
      return null;
    }
  };
  const vetSub = daysUntil(pet?.vet_wellness_next);

  return (
    <div className="mt-5 pt-5 border-t border-[color:var(--line-soft)]">
      {!editing ? (
        <div
          className="rounded-xl p-4 bg-[color:var(--bg-soft,#F5EFE2)] border border-[color:var(--line-soft)]"
          data-testid={`pet-care-schedule-card-${pet.id}`}
        >
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="label-tiny">Care schedule</div>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="ff-chip text-[12px]"
              data-testid={`pet-care-schedule-edit-${pet.id}`}
            >
              Edit
            </button>
          </div>
          <div className="space-y-2 text-sm text-[color:var(--ink)]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[color:var(--ink-2)]">Next vet exam</span>
              <span className="tabular">
                {pet?.vet_wellness_next ? (
                  <>
                    {pet.vet_wellness_next}
                    {vetSub && (
                      <span className="text-[color:var(--ink-3)] ml-1.5 text-[12px]">
                        · {vetSub}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-[color:var(--ink-3)]">not set</span>
                )}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[color:var(--ink-2)]">Flea/tick cycle</span>
              <span className="tabular">
                every {pet?.flea_tick_interval_days || 30} days
              </span>
            </div>
            {isCat && (
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[color:var(--ink-2)]">Litter tracking</span>
                <span className="tabular">
                  {pet?.litter_enabled ? "on" : "off"}
                </span>
              </div>
            )}
            {(pet?.food_reminder_enabled || pet?.water_reminder_enabled || true) && (
              <div className="pt-3 border-t border-[color:var(--line-soft)] mt-2 space-y-2">
                <div className="label-tiny mb-1">Optional reminders</div>
                <button
                  type="button"
                  onClick={() => toggleReminder("food")}
                  className="w-full flex items-start justify-between gap-3 text-left"
                  data-testid={`pet-care-food-reminder-row-${pet.id}`}
                >
                  <span className="flex-1">
                    <span className="block text-sm">Food nudge</span>
                    <span className="block label-tiny text-[color:var(--ink-3)] leading-snug mt-0.5">
                      Calm 07:30 ping (08:30 on weekends) — only if no one's
                      tapped <strong>fed</strong> for {pet?.name || "your pet"} in
                      24+ hours, so a normal feeding rhythm never triggers a nudge.
                    </span>
                  </span>
                  <span
                    className={`flex-shrink-0 mt-0.5 inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[11px] font-medium ${
                      foodReminder
                        ? "bg-[color:var(--ink)] text-[color:var(--bg)]"
                        : "bg-[color:var(--bg)] text-[color:var(--ink-3)] border border-[color:var(--line-soft)]"
                    }`}
                  >
                    {foodReminder ? "On" : "Off"}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => toggleReminder("water")}
                  className="w-full flex items-start justify-between gap-3 text-left"
                  data-testid={`pet-care-water-reminder-row-${pet.id}`}
                >
                  <span className="flex-1">
                    <span className="block text-sm">Fresh water</span>
                    <span className="block label-tiny text-[color:var(--ink-3)] leading-snug mt-0.5">
                      Calm 07:30 ping (08:30 on weekends) — only if water
                      hasn't been changed in 24+ hours.
                    </span>
                  </span>
                  <span
                    className={`flex-shrink-0 mt-0.5 inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[11px] font-medium ${
                      waterReminder
                        ? "bg-[color:var(--ink)] text-[color:var(--bg)]"
                        : "bg-[color:var(--bg)] text-[color:var(--ink-3)] border border-[color:var(--line-soft)]"
                    }`}
                  >
                    {waterReminder ? "On" : "Off"}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
      {editing && (
        <div className="mt-3 space-y-3">
          <div>
            <label className="label-tiny block mb-1">Next vet exam date</label>
            <input
              type="date"
              value={vetNext}
              onChange={(e) => setVetNext(e.target.value)}
              className="ff-input max-w-[220px]"
              data-testid={`pet-care-vet-next-input-${pet.id}`}
            />
          </div>
          <div>
            <label className="label-tiny block mb-1">Flea/tick treatment cycle</label>
            <div className="flex gap-2">
              {[30, 60, 90].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setFleaInterval(n)}
                  className={`ff-chip ${Number(fleaInterval) === n ? "is-active" : ""}`}
                  data-testid={`pet-care-flea-${n}`}
                >
                  {n} days
                </button>
              ))}
            </div>
          </div>
          {isCat && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={litterOn}
                onChange={(e) => setLitterOn(e.target.checked)}
                className="w-4 h-4 accent-[color:var(--ink)]"
                data-testid={`pet-care-litter-toggle-${pet.id}`}
              />
              <span className="text-sm">Track litter box changes (for indoor cats)</span>
            </label>
          )}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="ff-btn ff-btn-primary"
              data-testid={`pet-care-schedule-save-${pet.id}`}
            >
              {saving ? "Saving…" : "Save schedule"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="ff-btn"
              data-testid={`pet-care-schedule-cancel-${pet.id}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
